'use client';

import { Box, Grid, HStack, VStack } from '@terra/ui-styled-system';
import { Button, Text, TextDiffView } from '@terra/ui-design-system';
import { useTranslations } from 'next-intl';
import {
  type CredentialAuthMaterialEditableField,
  type PendingCredentialAuthMaterialFieldEdit,
  useCredentialAuthMaterialEditStore,
} from '../../store/credentialAuthMaterialEditStore';
import { useCredentialUpdateQueueStore } from '../../store/credentialUpdateQueueStore';
import {
  formatCredentialCookiesForDisplay,
  formatCredentialHeadersForDisplay,
} from '../../utils/credentialAuthMaterialDisplay.utils';

type AuthMaterialFieldPendingEditProps = {
  pendingEdit: PendingCredentialAuthMaterialFieldEdit;
  onAccept: () => void;
  onDiscard: () => void;
};

function AuthMaterialFieldPendingEdit({
  pendingEdit,
  onAccept,
  onDiscard,
}: AuthMaterialFieldPendingEditProps) {
  const tCommon = useTranslations('common');
  const tCredentials = useTranslations('designSystem.applicationCredentials');
  const fieldLabel =
    pendingEdit.field === 'headers'
      ? tCredentials('headers')
      : tCredentials('cookies');
  const beforeDisplay = formatAuthMaterialFieldValue(
    pendingEdit,
    pendingEdit.before,
  );
  const afterDisplay = formatAuthMaterialFieldValue(
    pendingEdit,
    pendingEdit.after,
  );

  return (
    <VStack
      gap={3}
      alignItems="stretch"
      p={4}
      borderRadius="lg"
      borderWidth="1px"
      borderColor="border.info"
      bg="bg.canvas"
    >
      <HStack justifyContent="space-between" alignItems="center" gap={2}>
        <Text fontSize="sm" fontWeight="semibold" color="fg.default">
          {fieldLabel}
        </Text>
        <HStack gap={1}>
          <Button size="xs" variant="ghost" onClick={onDiscard} type="button">
            {tCommon('discard')}
          </Button>
          <Button size="xs" variant="solid" onClick={onAccept} type="button">
            {tCommon('accept')}
          </Button>
        </HStack>
      </HStack>
      <Grid columns={2} gap={4}>
        <VStack gap={1} alignItems="stretch">
          <Text fontSize="xs" color="fg.muted">
            {tCommon('before')}
          </Text>
          <Box
            p={3}
            borderRadius="md"
            borderWidth="1px"
            borderColor="border.subtle"
            bg="bg.subtle"
            minH="80px"
          >
            <Text fontSize="sm" whiteSpace="pre-wrap">
              {beforeDisplay}
            </Text>
          </Box>
        </VStack>
        <VStack gap={1} alignItems="stretch">
          <Text fontSize="xs" color="fg.muted">
            {tCommon('after')}
          </Text>
          <Box
            p={3}
            borderRadius="md"
            borderWidth="1px"
            borderColor="border.info"
            bg="bg.canvas"
            minH="80px"
          >
            <TextDiffView before={beforeDisplay} after={afterDisplay} />
          </Box>
        </VStack>
      </Grid>
    </VStack>
  );
}

function formatAuthMaterialFieldValue(
  entry: PendingCredentialAuthMaterialFieldEdit,
  value: PendingCredentialAuthMaterialFieldEdit['before'],
): string {
  if (entry.field === 'headers') {
    return formatCredentialHeadersForDisplay(
      value as PendingCredentialAuthMaterialFieldEdit['before'] &
        {
          key: string;
          value: string;
        }[],
    );
  }
  return formatCredentialCookiesForDisplay(
    value as PendingCredentialAuthMaterialFieldEdit['before'] &
      {
        name: string;
        value: string;
      }[],
  );
}

type CredentialAuthMaterialCopilotPendingEditsSectionProps = {
  credentialId: string;
  credentialLabel: string;
  baseFields: {
    headers: Array<{ key: string; value: string }>;
    cookies: Array<{ name: string; value: string }>;
  };
  onAccepted: (nextFields: {
    headers: Array<{ key: string; value: string }>;
    cookies: Array<{ name: string; value: string }>;
  }) => void;
};

export function CredentialAuthMaterialCopilotPendingEditsSection({
  credentialId,
  credentialLabel,
  baseFields,
  onAccepted,
}: CredentialAuthMaterialCopilotPendingEditsSectionProps) {
  const tCommon = useTranslations('common');
  const tSettings = useTranslations('settings');
  const pendingAiEdits = useCredentialAuthMaterialEditStore(
    (state) => state.pendingAiEdits[credentialId],
  );
  const acceptAiEdits = useCredentialAuthMaterialEditStore(
    (state) => state.acceptAiEdits,
  );
  const discardAiEdits = useCredentialAuthMaterialEditStore(
    (state) => state.discardAiEdits,
  );

  if (!pendingAiEdits?.fields.length) {
    return null;
  }

  const handleAcceptAll = () => {
    const nextFields = acceptAiEdits(credentialId, undefined, {
      baseDraftFields: baseFields,
    });
    if (nextFields) {
      onAccepted(nextFields);
    }
  };

  const handleDiscardAll = () => {
    const pendingBeforeDiscard = pendingAiEdits;
    discardAiEdits(credentialId);
    const consumedSourceUpdateIds = [
      ...new Set(
        (pendingBeforeDiscard?.fields ?? [])
          .map((entry) => entry.sourceUpdateId)
          .filter(
            (id): id is string => typeof id === 'string' && id.length > 0,
          ),
      ),
    ];
    if (consumedSourceUpdateIds.length > 0) {
      useCredentialUpdateQueueStore
        .getState()
        .removeCredentialUpdatesByIds(consumedSourceUpdateIds);
    }
  };

  const handleAcceptField = (field: CredentialAuthMaterialEditableField) => {
    const nextFields = acceptAiEdits(credentialId, [field], {
      baseDraftFields: baseFields,
    });
    if (nextFields) {
      onAccepted(nextFields);
    }
  };

  const handleDiscardField = (field: CredentialAuthMaterialEditableField) => {
    const pendingBeforeDiscard = pendingAiEdits;
    discardAiEdits(credentialId, [field]);
    const remainingFields =
      useCredentialAuthMaterialEditStore
        .getState()
        .getPendingAiEdits(credentialId)?.fields ?? [];
    if (remainingFields.length > 0) {
      return;
    }
    const consumedSourceUpdateIds = [
      ...new Set(
        (pendingBeforeDiscard?.fields ?? [])
          .map((entry) => entry.sourceUpdateId)
          .filter(
            (id): id is string => typeof id === 'string' && id.length > 0,
          ),
      ),
    ];
    if (consumedSourceUpdateIds.length > 0) {
      useCredentialUpdateQueueStore
        .getState()
        .removeCredentialUpdatesByIds(consumedSourceUpdateIds);
    }
  };

  return (
    <VStack
      gap={3}
      alignItems="stretch"
      p={4}
      borderRadius="lg"
      borderWidth="1px"
      borderColor="border.info"
      bg="bg.subtle"
    >
      <HStack justifyContent="space-between" alignItems="center" gap={2}>
        <VStack gap={1} alignItems="flex-start">
          <Text fontSize="sm" fontWeight="semibold" color="fg.default">
            {tSettings('credentialAuthMaterialPendingEdits.proposalSummary', {
              count: pendingAiEdits.fields.length,
              credentialLabel,
            })}
          </Text>
          {pendingAiEdits.reason ? (
            <Text fontSize="xs" color="fg.muted">
              {pendingAiEdits.reason}
            </Text>
          ) : null}
        </VStack>
        <HStack gap={1}>
          <Button size="xs" variant="ghost" onClick={handleDiscardAll}>
            {tCommon('discardAll')}
          </Button>
          <Button size="xs" variant="solid" onClick={handleAcceptAll}>
            {tCommon('acceptAll')}
          </Button>
        </HStack>
      </HStack>
      {pendingAiEdits.fields.map((entry) => (
        <AuthMaterialFieldPendingEdit
          key={`${credentialId}-${entry.field}`}
          pendingEdit={entry}
          onAccept={() => handleAcceptField(entry.field)}
          onDiscard={() => handleDiscardField(entry.field)}
        />
      ))}
    </VStack>
  );
}
